import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionCheckRef = useRef<NodeJS.Timeout | null>(null);
  const lastActiveRef = useRef(Date.now());

  // Enhanced session check for PWAs
  const checkSession = async (isInitial = false) => {
    try {
      if (!supabase) {
        setLoading(false);
        return;
      }

      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.warn('Session check error:', error);
        setUser(null);
        setLoading(false);
        return;
      }

      setUser(session?.user ?? null);
      
      // Update last active time
      lastActiveRef.current = Date.now();
      
      if (isInitial) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Session check failed:', err);
      setUser(null);
      if (isInitial) {
        setLoading(false);
      }
    }
  };

  // Handle visibility change (PWA coming back from background)
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const timeSinceLastCheck = Date.now() - lastActiveRef.current;
      // If app was in background for more than 5 minutes, check session
      if (timeSinceLastCheck > 5 * 60 * 1000) {
        console.log('App returned from background, checking session...');
        checkSession();
      }
    }
  };

  // Handle page focus (additional PWA event)
  const handleFocus = () => {
    const timeSinceLastCheck = Date.now() - lastActiveRef.current;
    if (timeSinceLastCheck > 2 * 60 * 1000) { // 2 minutes
      checkSession();
    }
  };

  // Periodic session refresh for PWAs
  const startPeriodicCheck = () => {
    // Clear existing timer
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
    }
    
    // Check session every 10 minutes
    sessionCheckRef.current = setInterval(() => {
      if (user) {
        checkSession();
      }
    }, 10 * 60 * 1000);
  };

  useEffect(() => {
    // Initial session check
    checkSession(true);

    if (!supabase) {
      setLoading(false);
      return;
    }

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event, session?.user?.id);
      
      setUser(session?.user ?? null);
      
      // Handle specific events
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        lastActiveRef.current = Date.now();
      }
      
      // Restart periodic checks when signed in
      if (event === 'SIGNED_IN' && session?.user) {
        startPeriodicCheck();
      }
      
      // Clear periodic checks when signed out
      if (event === 'SIGNED_OUT') {
        if (sessionCheckRef.current) {
          clearInterval(sessionCheckRef.current);
          sessionCheckRef.current = null;
        }
      }
    });

    // PWA-specific event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    // Start periodic checks if user is logged in
    if (user) {
      startPeriodicCheck();
    }

    // Cleanup
    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, []);

  // Manual refresh function for troubleshooting
  const refreshSession = () => {
    checkSession();
  };

  return { 
    user, 
    loading,
    refreshSession
  };
} 